import { Inbox, type LucideIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import type { PresenceLevel } from "../core/session";
import { C, CATEGORY_KEYS, categoryStyle, rarityStyle } from "./styles";

/** 白色圆角面板 */
export function Panel({ children, padding = 24, radius = 28, background = C.surface, style, className }: { children: ReactNode; padding?: number; radius?: number; background?: string; style?: CSSProperties; className?: string }) {
  return (
    <div className={`kp-panel ${className ?? ""}`} style={{ padding, borderRadius: radius, background, ...style }}>
      {children}
    </div>
  );
}

/** 白色圆角卡片，内部是用 RowDivider 隔开的行 */
export function FormCard({ children, radius = 20, style }: { children: ReactNode; radius?: number; style?: CSSProperties }) {
  return (
    <div className="kp-form-card" style={{ borderRadius: radius, ...style }}>
      {children}
    </div>
  );
}

/** 卡片内的分割线 */
export function RowDivider() {
  return <div className="kp-divider" />;
}

/** 卡片内一行：左标题，右侧值（value 文字或 children 自定义）；传 onClick 时整行可点 */
export function ValueRow({ title, value, children, onClick, height = 48 }: { title: ReactNode; value?: ReactNode; children?: ReactNode; onClick?: () => void; height?: number }) {
  const content = (
    <>
      <span className="kp-row-title">{title}</span>
      <span className="kp-row-value">{children ?? value}</span>
    </>
  );
  if (onClick) {
    return (
      <button type="button" className="kp-row is-button" style={{ minHeight: height }} onClick={onClick}>
        {content}
      </button>
    );
  }
  return (
    <div className="kp-row" style={{ minHeight: height }}>
      {content}
    </div>
  );
}

/** 小节标题（"提醒""最近添加"） */
export function SectionLabel({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="kp-section-label">
      <span>{children}</span>
      {action}
    </div>
  );
}

/** 一级页面标题（28 号粗体） */
export function PageTitle({ children, trailing }: { children: ReactNode; trailing?: ReactNode }) {
  return (
    <div className="kp-page-title">
      <h1>{children}</h1>
      {trailing}
    </div>
  );
}

/** 页面容器：统一边距和间距（桌面端在 AppShell 里限制最大宽度） */
export function Page({ children, gap = 14 }: { children: ReactNode; gap?: number }) {
  return (
    <div className="kp-page" style={{ gap }}>
      {children}
    </div>
  );
}

/** 页面级空状态 */
export function EmptyHint({ icon: Icon = Inbox, text, children }: { icon?: LucideIcon; text: string; children?: ReactNode }) {
  return (
    <div className="kp-empty">
      <Icon size={34} color="var(--kp-disabled)" />
      <p>{text}</p>
      {children}
    </div>
  );
}

/** 进度条 */
export function BarProgress({ value, height = 10, track = C.chip, fill = C.success }: { value: number; height?: number; track?: string; fill?: string }) {
  const p = Math.max(0, Math.min(1, value || 0));
  return (
    <div className="kp-bar" style={{ height, background: track }} role="progressbar" aria-valuenow={Math.round(p * 100)} aria-valuemin={0} aria-valuemax={100}>
      <div style={{ width: `${p * 100}%`, background: fill }} />
    </div>
  );
}

/** 圆环进度 */
export function RingProgress({ value, size = 120, lineWidth = 14, track = C.dark3, fill = C.accent, children }: { value: number; size?: number; lineWidth?: number; track?: string; fill?: string; children?: ReactNode }) {
  const p = Math.max(0, Math.min(1, value || 0));
  const r = (size - lineWidth) / 2;
  const len = 2 * Math.PI * r;
  return (
    <div className="kp-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={lineWidth} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={fill}
          strokeWidth={lineWidth}
          strokeLinecap="round"
          strokeDasharray={`${len * p} ${len}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {children != null && <div className="kp-ring-center">{children}</div>}
    </div>
  );
}

/** 圆形头像（取名字首字） */
export function Avatar({ name, size = 36, tint = C.accent, ink = C.ink }: { name: string; size?: number; tint?: string; ink?: string }) {
  return (
    <span className="kp-avatar" style={{ width: size, height: size, fontSize: size * 0.44, background: tint, color: ink }}>
      {Array.from(name)[0] ?? "?"}
    </span>
  );
}

const PRESENCE_STYLE: Record<PresenceLevel, { dot: string; text: string; label: string }> = {
  online: { dot: C.success, text: "var(--kp-other-ink)", label: "在线" },
  standby: { dot: C.accent, text: C.gold, label: "待机" },
  offline: { dot: C.disabled, text: C.inkSoft, label: "离线" },
};

/** 在线状态小圆点 + 文字（text 省略时显示"在线 / 待机 / 离线"） */
export function OnlineDot({ level, text }: { level: PresenceLevel; text?: string }) {
  const s = PRESENCE_STYLE[level];
  return (
    <span className="kp-online" style={{ color: s.text }}>
      <span style={{ background: s.dot }} />
      {text ?? s.label}
    </span>
  );
}

/** 星星图标（积分） */
export function StarIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 2.8l2.75 5.57 6.15.9-4.45 4.33 1.05 6.12L12 16.83l-5.5 2.89 1.05-6.12L3.1 9.27l6.15-.9z"
        fill="var(--kp-accent)"
        stroke="var(--kp-star-stroke)"
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** 日程类别图例 */
export function CategoryLegend() {
  return (
    <div className="kp-legend">
      {CATEGORY_KEYS.map((k) => {
        const s = categoryStyle(k);
        return (
          <span key={k}>
            <i style={{ background: s.dot }} />
            {s.name}
          </span>
        );
      })}
    </div>
  );
}

/** 日程类别色块（左侧方块，放图标） */
export function CategoryBlock({ category, size = 40, children }: { category: string; size?: number; children?: ReactNode }) {
  const s = categoryStyle(category);
  return (
    <span className="kp-block" style={{ width: size, height: size, background: s.tint, color: s.ink }}>
      {children}
    </span>
  );
}

/** 卡牌缩略卡面：art 不为空时显示生成的卡面图（docs/卡面生成.md），否则显示 glyph 大字 */
export function CardFace({ glyph, name, rarity, count = 1, owned = true, art }: { glyph: string; name: string; rarity: string; count?: number; owned?: boolean; art?: ReactNode }) {
  const r = rarityStyle(rarity);
  if (!owned) {
    return (
      <div className="kp-card-face is-locked">
        <span className="kp-num">?</span>
        <small>还没得到</small>
      </div>
    );
  }
  return (
    <div className="kp-card-face">
      <div className={`kp-card-art${art ? " has-image" : ""}`} style={{ background: art ? `color-mix(in srgb, ${r.tint} 35%, transparent)` : r.tint, color: r.ink }}>
        {art ?? glyph}
        {count > 1 && <span className="kp-card-count kp-num">×{count}</span>}
      </div>
      <div className="kp-card-meta">
        <span>{name}</span>
        <em style={{ background: r.ink }}>{r.name}</em>
      </div>
    </div>
  );
}
